import React, { Suspense, useEffect } from 'react'
import { Routes, Route, Navigate, useLocation } from 'react-router-dom'
import { ToastContainer } from 'react-toastify'
import 'react-toastify/dist/ReactToastify.css'

import { useAppSelector } from '@/hooks/redux'
import { useFeatureFlags } from '@/hooks/useFeatureFlags'
import { UserRole } from '@/types/enums'
import { isRunningAsPWA } from '@/utils/pwaDetection'
import Layout from '@/components/Layout'
import PublicLayout from '@/components/PublicLayout'
import ScrollToTop from '@/components/ScrollToTop'
import LandingPage from '@/pages/LandingPage'
import PricingPage from '@/pages/PricingPage'
import FeaturesPage from '@/pages/FeaturesPage'
import AboutPage from '@/pages/AboutPage'
import ContactPage from '@/pages/ContactPage'
import PrivacyPage from '@/pages/PrivacyPage'
import TermsPage from '@/pages/TermsPage'
import DocumentationPage from '@/pages/DocumentationPage'
import LoginPage from '@/pages/LoginPage'
import RegisterPage from '@/pages/RegisterPage'
import ForgotPasswordPage from '@/pages/ForgotPasswordPage'
import ResetPasswordPage from '@/pages/ResetPasswordPage'
import VerifyEmailPage from '@/pages/VerifyEmailPage'
import VerifyEmailPendingPage from '@/pages/VerifyEmailPendingPage'
import ForceChangePasswordPage from '@/pages/ForceChangePasswordPage'
import DashboardPage from '@/pages/DashboardPage'
import PlayersPage from '@/pages/PlayersPage'
import PlayerProfilePage from '@/pages/PlayerProfilePage'
import SchedulePage from '@/pages/SchedulePage'
import AnalysisPage from '@/pages/AnalysisPage'
import LeagueManagementPage from '@/pages/LeagueManagementPage'
import LeagueAnalysisPage from '@/pages/LeagueAnalysisPage'
import TeamAnalysisPage from '@/pages/TeamAnalysisPage'
import SuperAdminPage from '@/pages/SuperAdminPage'
const DevConsolePage = React.lazy(() => import('@/pages/DevConsolePage'))
import SettingsPage from '@/pages/SettingsPage'
import UserSettingsPage from '@/pages/UserSettingsPage'
import AcceptInvitationPage from '@/pages/AcceptInvitationPage'
import SessionExpirationWarning from '@/components/SessionExpirationWarning'
import OAuthCallback from '@/pages/OAuthCallback'
import { startListening as startOfflineQueueListener } from '@/utils/offlineMutationQueue'
import { ThemeProvider } from '@mui/material/styles'
import modernTheme from '@/theme/modernTheme'

// Wrapper that applies the modern public theme to auth pages
const ModernThemed: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <ThemeProvider theme={modernTheme}>{children}</ThemeProvider>
)

function App() {
  const { isAuthenticated, user } = useAppSelector((state) => state.auth)
  const { positionAnalysisEnabled } = useFeatureFlags()
  const location = useLocation()
  const isPWA = isRunningAsPWA()

  // Start offline mutation queue replay listener once
  useEffect(() => {
    startOfflineQueueListener();
  }, []);

  if (!isAuthenticated) {
    // In PWA mode, skip landing page and go to login
    // /app is the PWA start_url - redirect to login
    const shouldSkipLanding = isPWA || location.pathname === '/app'

    // Build a returnTo param so login can redirect back to the original deep link
    const returnTo = location.pathname !== '/login' && location.pathname !== '/' && location.pathname !== '/app'
      ? `?returnTo=${encodeURIComponent(location.pathname + location.search)}`
      : ''

    return (
      <>
        <ScrollToTop />
        <Routes>
          {/* Public pages with shared navbar */}
          <Route element={<PublicLayout />}>
            <Route
              path="/"
              element={shouldSkipLanding ? <Navigate to="/login" replace /> : <LandingPage />}
            />
            <Route path="/pricing" element={<PricingPage />} />
            <Route path="/features" element={<FeaturesPage />} />
            <Route path="/about" element={<AboutPage />} />
            <Route path="/contact" element={<ContactPage />} />
            <Route path="/privacy" element={<PrivacyPage />} />
            <Route path="/terms" element={<TermsPage />} />
          </Route>
          {/* PWA entry point - redirect to login when not authenticated */}
          <Route path="/app" element={<Navigate to="/login" replace />} />
          <Route path="/documentation" element={<ModernThemed><DocumentationPage /></ModernThemed>} />
          <Route path="/login" element={<ModernThemed><LoginPage /></ModernThemed>} />
          <Route path="/register" element={<ModernThemed><RegisterPage /></ModernThemed>} />
          <Route path="/forgot-password" element={<ModernThemed><ForgotPasswordPage /></ModernThemed>} />
          <Route path="/reset-password" element={<ModernThemed><ResetPasswordPage /></ModernThemed>} />
          <Route path="/verify-email" element={<ModernThemed><VerifyEmailPage /></ModernThemed>} />
          <Route path="/activation-success" element={<ModernThemed><VerifyEmailPage /></ModernThemed>} />
          <Route path="/accept-invitation" element={<ModernThemed><AcceptInvitationPage /></ModernThemed>} />
          <Route path="/auth/callback" element={<OAuthCallback />} />
          <Route path="/dashboard" element={<Navigate to={`/login${returnTo}`} replace />} />
          <Route
            path="*"
            element={<Navigate to={shouldSkipLanding ? `/login${returnTo}` : '/'} replace />}
          />
        </Routes>
        <ToastContainer
          position="top-right"
          autoClose={5000}
          hideProgressBar={false}
          newestOnTop={false}
          closeOnClick
          rtl={false}
          pauseOnFocusLoss
          draggable
          pauseOnHover
          style={{ marginTop: '64px' }}
        />
      </>
    )
  }

  // Check if user needs to verify email (super admins are exempt)
  const needsEmailVerification = user?.emailVerified === false && user?.role !== UserRole.SUPER_ADMIN

  if (needsEmailVerification) {
    return (
      <>
        <ScrollToTop />
        <Routes>
          <Route path="/verify-email-pending" element={<ModernThemed><VerifyEmailPendingPage /></ModernThemed>} />
          <Route path="/verify-email" element={<ModernThemed><VerifyEmailPage /></ModernThemed>} />
          <Route path="/activation-success" element={<ModernThemed><VerifyEmailPage /></ModernThemed>} />
          <Route path="/forgot-password" element={<ModernThemed><ForgotPasswordPage /></ModernThemed>} />
          <Route path="/reset-password" element={<ModernThemed><ResetPasswordPage /></ModernThemed>} />
          <Route path="*" element={<Navigate to="/verify-email-pending" replace />} />
        </Routes>
        <ToastContainer
          position="top-right"
          autoClose={5000}
          hideProgressBar={false}
          newestOnTop={false}
          closeOnClick
          rtl={false}
          pauseOnFocusLoss
          draggable
          pauseOnHover
          style={{ marginTop: '64px' }}
        />
      </>
    )
  }

  // Check if user must change their password (e.g. bootstrap admin on first login)
  if (user?.mustChangePassword) {
    return (
      <>
        <ScrollToTop />
        <Routes>
          <Route path="/change-password" element={<ModernThemed><ForceChangePasswordPage /></ModernThemed>} />
          <Route path="*" element={<Navigate to="/change-password" replace />} />
        </Routes>
        <ToastContainer
          position="top-right"
          autoClose={5000}
          hideProgressBar={false}
          newestOnTop={false}
          closeOnClick
          rtl={false}
          pauseOnFocusLoss
          draggable
          pauseOnHover
          style={{ marginTop: '64px' }}
        />
      </>
    )
  }

  return (
    <>
      <ScrollToTop />
      <Routes>
        {/* Auth pages rendered outside Layout */}
        <Route path="/forgot-password" element={<ForgotPasswordPage />} />
        <Route path="/reset-password" element={<ResetPasswordPage />} />
        <Route path="/verify-email" element={<VerifyEmailPage />} />
        <Route path="/activation-success" element={<VerifyEmailPage />} />

        {/* PWA entry point - redirect to dashboard when authenticated */}
        <Route path="/app" element={<Navigate to="/dashboard" replace />} />

        {/* Main app pages with Layout */}
        <Route path="*" element={
          <Layout>
            <Routes>
              <Route path="/dashboard" element={<DashboardPage />} />
              <Route path="/players" element={<PlayersPage />} />
              <Route path="/players/:playerId" element={<PlayerProfilePage />} />
              <Route path="/schedule" element={<SchedulePage />} />
              <Route path="/leagues" element={<LeagueManagementPage />} />
              <Route path="/leagues/:leagueId/analysis" element={<LeagueAnalysisPage />} />
              {positionAnalysisEnabled && (
                <>
                  <Route path="/leagues/:leagueId/teams/:teamId/analysis" element={<TeamAnalysisPage />} />
                  <Route path="/analysis/:type/:id" element={<AnalysisPage />} />
                </>
              )}
              <Route path="/settings" element={<SettingsPage />} />
              <Route path="/user-settings" element={<UserSettingsPage />} />
              <Route path="/accept-invitation" element={<AcceptInvitationPage />} />
              <Route path="/pricing" element={<PricingPage />} />
              {user?.role === UserRole.SUPER_ADMIN && (
                <Route path="/admin" element={<SuperAdminPage />} />
              )}
              {import.meta.env.DEV && (
                <Route path="/dev" element={<Suspense fallback={null}><DevConsolePage /></Suspense>} />
              )}
              {/* Legacy redirects */}
              <Route path="/competitions" element={<Navigate to="/leagues" replace />} />
              <Route path="/competitions-vnext" element={<Navigate to="/leagues" replace />} />
              <Route path="/tournaments" element={<Navigate to="/leagues" replace />} />
              <Route path="/analysis" element={<Navigate to="/leagues" replace />} />
              <Route path="/" element={<Navigate to="/dashboard" replace />} />
              <Route path="*" element={<Navigate to="/dashboard" replace />} />
            </Routes>
          </Layout>
        } />
      </Routes>
      <SessionExpirationWarning />
      <ToastContainer
        position="top-right"
        autoClose={5000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        style={{ marginTop: '64px' }}
      />
    </>
  )
}

export default App
